/**
 * Native FCM push notifications hook for the Capacitor iOS/Android shell.
 *
 * Works alongside the existing Web Push (VAPID) implementation in `push.js`.
 * On the web this hook is a no-op — the Capacitor plugin isn't available in
 * browsers, so we short-circuit on `Capacitor.isNativePlatform() === false`.
 *
 * Flow:
 *   1. Ask the OS for notification permission on first mount (once the user
 *      is logged in — otherwise there's no user to bind the token to).
 *   2. Call `PushNotifications.register()` to trigger the platform-native
 *      registration handshake with APNs (iOS) or FCM (Android).
 *   3. Capture the FCM token in the `registration` listener and POST it to
 *      the backend at `/push/fcm/register`.
 *   4. On notification tap, read `data.route` and navigate the SPA there so
 *      "someone approved your application" opens the vendor dashboard, etc.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

async function registerNativePush(navigate, setListenersRef) {
  const { PushNotifications } = await import("@capacitor/push-notifications");

  // 1) Permission gate
  let permStatus = await PushNotifications.checkPermissions();
  if (permStatus.receive === "prompt") {
    permStatus = await PushNotifications.requestPermissions();
  }
  if (permStatus.receive !== "granted") {
    // User denied — silent. They can re-enable from OS settings.
    return;
  }

  // 2) Registration handshake
  try {
    await PushNotifications.register();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[native-push] register() failed:", e);
    return;
  }

  // 3) Token listener → send to backend
  const regHandle = await PushNotifications.addListener("registration", async (token) => {
    try {
      await api.post("/push/fcm/register", {
        token: token.value,
        platform: Capacitor.getPlatform(),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[native-push] backend registration failed:", e);
    }
  });

  const regErrHandle = await PushNotifications.addListener("registrationError", (err) => {
    // eslint-disable-next-line no-console
    console.warn("[native-push] registrationError:", err);
  });

  // 4) Deep-link on tap
  const tapHandle = await PushNotifications.addListener(
    "pushNotificationActionPerformed",
    (event) => {
      const data = event?.notification?.data || {};
      const route = data.route || data.deepLink;
      if (route && typeof route === "string" && route.startsWith("/")) {
        navigate(route);
      }
    },
  );

  // In-app foreground receive — leave to the OS default banner. If we ever
  // want to render a toast here, add a `pushNotificationReceived` listener.

  setListenersRef.current = [regHandle, regErrHandle, tapHandle];
}

export function useNativePush() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const listenersRef = { current: [] };

  useEffect(() => {
    if (!Capacitor?.isNativePlatform?.()) return;
    if (!user) return; // wait until the user is authenticated

    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await registerNativePush(navigate, listenersRef);
    })();

    return () => {
      cancelled = true;
      for (const h of listenersRef.current) {
        try { h.remove?.(); } catch { /* ignore */ }
      }
      listenersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}
